import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { 
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const ReportBuilder = () => {
  const [report, setReport] = useState(null);
  const [reportItems, setReportItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reportRes, itemsRes, availableRes, categoriesRes] = await Promise.all([
        api.get(`/price-book/reports/${id}`),
        api.get(`/price-book/reports/${id}/items`),
        api.get('/price-book/items'),
        api.get('/price-book/categories')
      ]);
      
      setReport(reportRes.data);
      setReportItems(itemsRes.data.items || []);
      setAvailableItems(availableRes.data);
      setCategories(categoriesRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      if (err.response?.status === 404) {
        setError('Report not found');
      } else {
        setError('Failed to load report data');
      }
    } finally {
      setLoading(false);
    }
  };

  const addItemToReport = async (itemId) => {
    try {
      setSaving(true);
      await api.post(`/price-book/reports/${id}/items`, {
        item_id: itemId,
        display_order: reportItems.length + 1
      });
      
      // Refresh the report items
      const response = await api.get(`/price-book/reports/${id}/items`);
      setReportItems(response.data.items || []);
      setShowItemSelector(false);
    } catch (err) {
      console.error('Error adding item:', err);
      alert('Failed to add item to report');
    } finally {
      setSaving(false);
    }
  };

  const removeItemFromReport = async (itemId) => {
    if (!window.confirm('Are you sure you want to remove this item from the report?')) {
      return;
    }

    try {
      setSaving(true);
      await api.delete(`/price-book/reports/${id}/items/${itemId}`);
      
      // Refresh the report items
      const response = await api.get(`/price-book/reports/${id}/items`);
      setReportItems(response.data.items || []);
    } catch (err) {
      console.error('Error removing item:', err);
      alert('Failed to remove item from report');
    } finally {
      setSaving(false);
    }
  };

  const moveItem = async (itemId, direction) => {
    const currentIndex = reportItems.findIndex(item => item.id === itemId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= reportItems.length) return;

    // Create new order array
    const newItems = [...reportItems];
    [newItems[currentIndex], newItems[newIndex]] = [newItems[newIndex], newItems[currentIndex]];

    // Update display orders
    const itemOrders = newItems.map((item, index) => ({
      itemId: item.id,
      displayOrder: index + 1
    }));

    try {
      setSaving(true);
      await api.put(`/price-book/reports/${id}/items/order`, { item_orders: itemOrders });
      
      // Update local state
      setReportItems(newItems);
    } catch (err) {
      console.error('Error reordering items:', err);
      alert('Failed to reorder items');
    } finally {
      setSaving(false);
    }
  };

  const filteredAvailableItems = availableItems.filter(item => {
    // Don't show items already in the report
    if (reportItems.some(reportItem => reportItem.id === item.id)) {
      return false;
    }

    // Filter by search term
    if (searchTerm && !item.item_name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Filter by category
    if (selectedCategory && item.category_id !== parseInt(selectedCategory)) {
      return false;
    }

    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={() => navigate('/price-book/reports')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div className="sm:flex-auto">
          <div className="flex items-center space-x-3 mb-4">
            <button
              onClick={() => navigate('/price-book/reports')}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Reports
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Manage Report Items</h1>
          <p className="mt-2 text-sm text-gray-700">
            {report?.name} - Add, remove, and reorder items in this report
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <div className="flex space-x-3">
            <Link
              to={`/price-book/reports/${id}`}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              View Report
            </Link>
            <button
              onClick={() => setShowItemSelector(true)}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Items
            </button>
          </div>
        </div>
      </div>

      {/* Current Items */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Report Items ({reportItems.length})
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Items currently included in this report
          </p>
        </div>

        {reportItems.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No items in report</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding some price book items to this report.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowItemSelector(true)}
                className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Items
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {reportItems.map((item, index) => (
              <div key={item.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-sm font-medium text-gray-600 mr-4">
                      {index + 1}
                    </span>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{item.item_name}</h4>
                      {item.description && (
                        <p className="text-sm text-gray-500">{item.description}</p>
                      )}
                      <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                        <span>Category: {item.category_display_label || 'Uncategorized'}</span>
                        <span>Tiers: {item.tier_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => moveItem(item.id, 'up')}
                    disabled={index === 0 || saving}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ChevronUpIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => moveItem(item.id, 'down')}
                    disabled={index === reportItems.length - 1 || saving}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => removeItemFromReport(item.id)}
                    disabled={saving}
                    className="p-1 text-red-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Remove from report"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Item Selector Modal */}
      {showItemSelector && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Add Items to Report</h3>
              <button
                onClick={() => setShowItemSelector(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Search and Filters */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex space-x-4">
                <div className="flex-1">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="w-48">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.display_label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Available Items */}
            <div className="px-6 py-4 max-h-96 overflow-y-auto">
              {filteredAvailableItems.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">
                    {availableItems.length === reportItems.length 
                      ? 'All available items are already in this report'
                      : 'No items match your search criteria'
                    }
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredAvailableItems.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">{item.item_name}</h4>
                        {item.description && (
                          <p className="text-sm text-gray-500">{item.description}</p>
                        )}
                        <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                          <span>Category: {item.category_display_label || 'Uncategorized'}</span>
                          <span>Tiers: {item.tier_count || 0}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => addItemToReport(item.id)}
                        disabled={saving}
                        className="ml-4 inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowItemSelector(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportBuilder; 